import { useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronLeft } from "lucide-react";
import { cn } from "../lib/cn";
import { CenteredSpinner, EmptyState, ErrorBanner } from "./primitives";

export interface ListDetailProps<T> {
  items: T[];
  itemKey: (item: T) => string;
  renderItem: (ctx: { item: T; isSelected: boolean; index: number }) => React.ReactNode;
  renderDetail: (item: T) => React.ReactNode;
  /** Rendered in the detail pane when `selectedId` is non-null but does
   *  not match any list item (typically `"__new__"` for create flows).
   *  When omitted, the standard `detailEmpty` placeholder is shown. */
  renderCreating?: () => React.ReactNode;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  emptyState?: React.ReactNode;
  detailEmpty?: React.ReactNode;
  toolbar?: React.ReactNode;
  itemHeight?: number;
  loading?: boolean;
  error?: Error | null;
  listWidthClass?: string;
}

export function ListDetail<T>({
  items,
  itemKey,
  renderItem,
  renderDetail,
  renderCreating,
  selectedId,
  onSelect,
  emptyState,
  detailEmpty,
  toolbar,
  itemHeight = 64,
  loading,
  error,
  listWidthClass = "md:w-[28rem]",
}: ListDetailProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 8,
    getItemKey: (i) => itemKey(items[i]),
  });

  const selectedItem = items.find((it) => itemKey(it) === selectedId) ?? null;

  // Keyboard nav: j/k/Up/Down + Enter to confirm; Escape to clear selection
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const len = items.length;
      if (len === 0) return;
      const currentIdx = selectedId ? items.findIndex((it) => itemKey(it) === selectedId) : -1;
      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        const next = currentIdx < 0 ? 0 : Math.min(currentIdx + 1, len - 1);
        onSelect(itemKey(items[next]));
        virtualizer.scrollToIndex(next, { align: "auto" });
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        const next = currentIdx <= 0 ? 0 : currentIdx - 1;
        onSelect(itemKey(items[next]));
        virtualizer.scrollToIndex(next, { align: "auto" });
      } else if (e.key === "Escape") {
        onSelect(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items, selectedId, onSelect, itemKey, virtualizer]);

  // On mobile this is a single-pane view: the list takes the full width and,
  // once something is selected, the detail replaces it (with a back bar).
  // On md+ both panes show side by side as before. `detailOpen` drives which
  // pane is visible on small screens.
  const detailOpen = selectedId !== null;

  return (
    <div className="flex h-full min-h-0 min-w-0 gap-0 flex-1">
      {/* List pane — full width on mobile, fixed on desktop. Hidden on mobile
          while a detail/create view is open. */}
      <div className={cn(
        "flex-col min-h-0 md:flex-shrink-0 border-r border-border bg-surface w-full",
        listWidthClass,
        detailOpen ? "hidden md:flex" : "flex",
      )}>
        {toolbar && <div className="border-b border-border bg-surface flex-shrink-0">{toolbar}</div>}
        <div ref={parentRef} className="flex-1 overflow-y-auto min-h-0">
          {loading && items.length === 0 && <CenteredSpinner />}
          {error && (
            <div className="p-4">
              <ErrorBanner error={error} />
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="p-4">{emptyState ?? <EmptyState icon={<span className="text-2xl">∅</span>} title="No items" />}</div>
          )}
          {items.length > 0 && (
            <div
              style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}
            >
              {virtualizer.getVirtualItems().map((v) => {
                const item = items[v.index];
                const id = itemKey(item);
                const isSelected = id === selectedId;
                return (
                  <div
                    key={v.key}
                    data-index={v.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${v.start}px)`,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(isSelected ? null : id)}
                      className={cn(
                        "w-full text-left border-b border-border transition-colors block ring-focus",
                        isSelected ? "bg-accent-subtle/40" : "hover:bg-surface-2",
                      )}
                    >
                      {renderItem({ item, isSelected, index: v.index })}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detail pane — hidden on mobile until something is selected. */}
      <div className={cn(
        "flex-1 min-w-0 min-h-0 flex-col bg-app",
        detailOpen ? "flex" : "hidden md:flex",
      )}>
        {/* Mobile-only back bar so there's a way out of the detail view. */}
        {detailOpen && (
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="md:hidden flex items-center gap-1 px-3 py-2.5 border-b border-border text-sm text-fg-muted hover:text-fg bg-surface flex-shrink-0 ring-focus"
          >
            <ChevronLeft className="size-4" />
            Back
          </button>
        )}
        {selectedItem ? (
          <div className="flex-1 min-h-0 overflow-y-auto animate-fade-in">{renderDetail(selectedItem)}</div>
        ) : selectedId !== null && renderCreating ? (
          <div className="flex-1 min-h-0 overflow-y-auto animate-fade-in">{renderCreating()}</div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-fg-muted text-sm">
            {detailEmpty ?? <EmptyState icon={<span className="text-2xl text-fg-subtle">→</span>} title="Select an item" description="Use j/k to navigate the list." />}
          </div>
        )}
      </div>
    </div>
  );
}
